import { School } from 'lucide-react';
import { Modal } from '../../../components/ui/Modal';
import type { AuditModalState } from './master.types';
import { AuditDetailPanel } from './AuditDetailPanel';

interface AuditModalProps {
  auditModal: AuditModalState;
  onClose: () => void;
  onFilter: (rawDate: string, page: number, filtro: 'todos' | 'escolas' | 'usuarios') => void;
  selectedLog: any | null;
  setSelectedLog: (log: any | null) => void;
}

export function AuditModal({ auditModal, onClose, onFilter, selectedLog, setSelectedLog }: AuditModalProps) {
  const resourceColors: Record<string, string> = {
    auth: '#6366f1', aluno: '#10b981', escola: '#3b82f6', turma: '#8b5cf6',
    chamada: '#f59e0b', professor: '#14b8a6', default: '#64748b',
  };
  const acaoLabel: Record<string, string> = {
    VIEW: 'Visualizou', CREATE: 'Criou', UPDATE: 'Atualizou', DELETE: 'Deletou',
    PATCH: 'Alterou', LOGIN_SUCCESS: 'Login realizado', LOGIN_FAILED: 'Login falhou',
    LOGOUT: 'Logout', SECURITY_BLOCK: 'Bloqueio de segurança',
  };
  const recursoLabel: Record<string, string> = {
    auth: 'Autenticação', aluno: 'Aluno', escola: 'Escola', turma: 'Turma',
    chamada: 'Chamada', professor: 'Professor', admin: 'Administração',
    escolas: 'Escolas', usuarios: 'Usuários',
  };

  return (
    <Modal open={auditModal.open} onClose={onClose} title="Auditoria de Acessos" size="xl">
      <div style={{ padding: '0 4px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
          <div style={{ padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: 8, border: '1px solid #e2e8f0', flex: 1, minWidth: 200 }}>
            <div style={{ fontSize: '.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Data</div>
            <div style={{ fontSize: '1rem', color: '#0f172a', fontWeight: 700, textTransform: 'capitalize' }}>{auditModal.dateLabel}</div>
          </div>
          <div style={{ padding: '12px 16px', backgroundColor: '#f0f0ff', borderRadius: 8, border: '1px solid #c7d2fe', textAlign: 'right' }}>
            <div style={{ fontSize: '.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Total</div>
            <div style={{ fontSize: '1.25rem', color: '#6366f1', fontWeight: 800 }}>{auditModal.total} eventos</div>
          </div>
        </div>

        {/* Filtros */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          <span style={{ fontSize: '.8rem', color: '#64748b', fontWeight: 600, alignSelf: 'center', marginRight: 4 }}>Filtrar:</span>
          {(['todos', 'escolas', 'usuarios'] as const).map(f => (
            <button key={f} onClick={() => onFilter(auditModal.rawDate, 1, f)} style={{
              padding: '5px 12px', borderRadius: 6, border: '1px solid',
              fontSize: '.8rem', fontWeight: 600, cursor: 'pointer',
              borderColor: auditModal.filtro === f ? '#6366f1' : '#e2e8f0',
              background: auditModal.filtro === f ? '#6366f1' : 'transparent',
              color: auditModal.filtro === f ? 'white' : '#64748b',
              transition: 'all .2s',
            }}>
              {f === 'todos' ? 'Todos' : f === 'escolas' ? 'Escolas' : 'Usuários'}
            </button>
          ))}
        </div>

        {/* Lista */}
        {auditModal.loading ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: '#94a3b8' }}>Carregando...</div>
        ) : auditModal.logs.length === 0 ? (
          <div style={{ padding: '32px 0', textAlign: 'center', color: '#94a3b8' }}>Nenhum evento encontrado para este filtro.</div>
        ) : (
          <div style={{ display: 'flex', gap: 0, minHeight: 200 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: selectedLog ? '0 0 55%' : '1', transition: 'flex .25s', overflow: 'auto', maxHeight: 480, paddingRight: 8 }}>
              {auditModal.logs.map((log, i) => {
                const hora = new Date(log.criadoEm).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                const nomeUsuario = log.usuario?.nome || 'Sistema';
                const perfilUsuario = log.usuario?.cargo || log.usuario?.perfil || log.perfilUsuario || 'Sistema';
                const escolaNome = log.usuario?.escola?.nome || (log.escola ? 'Escola vinculada' : null);
                const fotoUrl = log.usuario?.fotoUrl;
                const inicial = nomeUsuario.charAt(0).toUpperCase();
                const dotColor = resourceColors[log.recurso] || resourceColors.default;
                const acaoTraduzida = acaoLabel[log.acao] || log.acao;
                const det = log.detalhes as Record<string, any> | null;
                const assunto = det?.nome || det?.email || det?.titulo || det?.descricao || det?.code ||
                  (log.recursoId ? `ID: ${String(log.recursoId).slice(0, 8)}\u2026` : null);
                const recursoTraduzido = recursoLabel[log.recurso] || log.recurso;
                const isSelected = selectedLog?.id === log.id;

                return (
                  <div key={log.id || i} onClick={() => setSelectedLog(isSelected ? null : log)} style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: '9px 12px', backgroundColor: isSelected ? '#f0f0ff' : 'white',
                    border: `1px solid ${isSelected ? '#c7d2fe' : '#e2e8f0'}`, borderRadius: 10,
                    cursor: 'pointer', transition: 'all .15s',
                  }}>
                    <span style={{ fontSize: '.78rem', fontWeight: 700, color: '#64748b', minWidth: 38 }}>{hora}</span>
                    <div style={{ width: 7, height: 7, borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: '.84rem', fontWeight: 700, color: '#0f172a', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                        {acaoTraduzida}
                        <span style={{ fontSize: '.7rem', fontWeight: 600, color: dotColor, background: `${dotColor}15`, padding: '1px 5px', borderRadius: 4 }}>{recursoTraduzido}</span>
                        {assunto && <span style={{ fontSize: '.75rem', color: '#475569', fontWeight: 500 }}>— {assunto}</span>}
                      </div>
                      {(escolaNome || log.ip) && (
                        <div style={{ fontSize: '.7rem', color: '#94a3b8', marginTop: 1, display: 'flex', gap: 6 }}>
                          {escolaNome && <span style={{ display: 'flex', alignItems: 'center', gap: 2 }}><School size={9} /> {escolaNome}</span>}
                          {log.ip && <span>IP {log.ip}</span>}
                        </div>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '.78rem', fontWeight: 700, color: '#1e293b' }}>{nomeUsuario}</div>
                        <div style={{ fontSize: '.68rem', color: '#94a3b8' }}>{perfilUsuario}</div>
                      </div>
                      {fotoUrl ? (
                        <img src={fotoUrl} alt={nomeUsuario} style={{ width: 28, height: 28, borderRadius: '50%', objectFit: 'cover', border: '2px solid #e2e8f0' }} />
                      ) : (
                        <div style={{ width: 28, height: 28, borderRadius: '50%', backgroundColor: '#6366f115', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6366f1', fontSize: '.78rem', fontWeight: 800, border: '1px solid #c7d2fe' }}>
                          {inicial}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {selectedLog && <AuditDetailPanel log={selectedLog} onClose={() => setSelectedLog(null)} />}
          </div>
        )}

        {/* Paginação */}
        {auditModal.totalPages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 20 }}>
            <button disabled={auditModal.page <= 1 || auditModal.loading}
              onClick={() => onFilter(auditModal.rawDate, auditModal.page - 1, auditModal.filtro)}
              style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: 'white', cursor: auditModal.page <= 1 ? 'not-allowed' : 'pointer', color: '#475569', fontSize: '.8rem', fontWeight: 600, opacity: auditModal.page <= 1 ? 0.4 : 1 }}>
              ← Anterior
            </button>
            <span style={{ fontSize: '.8rem', color: '#64748b', fontWeight: 600 }}>
              Pág {auditModal.page} de {auditModal.totalPages}
            </span>
            <button disabled={auditModal.page >= auditModal.totalPages || auditModal.loading}
              onClick={() => onFilter(auditModal.rawDate, auditModal.page + 1, auditModal.filtro)}
              style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: 'white', cursor: auditModal.page >= auditModal.totalPages ? 'not-allowed' : 'pointer', color: '#475569', fontSize: '.8rem', fontWeight: 600, opacity: auditModal.page >= auditModal.totalPages ? 0.4 : 1 }}>
              Próxima →
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}
